// src/AuthAssist.jsx
import { useEffect, useState } from "react";
import { auth } from "./firebase.js";
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";

export default function AuthAssist() {
  const [mode, setMode] = useState("signup"); // 'signin' | 'signup'
  const [email, setEmail] = useState("");
  const [invitedEmail, setInvitedEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [userEmail, setUserEmail] = useState("");

  // Pick up hints from URL / sessionStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlMode = (params.get("auth") || "").toLowerCase();
    const urlEmail = params.get("email") || "";

    const storedEmail = sessionStorage.getItem("inviteEmail") || "";
    const chosenEmail = (urlEmail || storedEmail).trim();

    if (urlMode === "signin" || urlMode === "signup") setMode(urlMode);
    if (chosenEmail) setEmail(chosenEmail);
    setInvitedEmail(chosenEmail.toLowerCase());

    const unsub = onAuthStateChanged(auth, (u) => {
      setUserEmail(u?.email || "");
    });
    return () => unsub();
  }, []);

  const card = { maxWidth: 520, margin: "40px auto", padding: 16, border: "1px solid #eee", borderRadius: 12 };
  const btn = { background: "#00B074", color: "#fff", border: "none", padding: "10px 14px", borderRadius: 10, fontWeight: 700, cursor: "pointer" };
  const btnAlt = { ...btn, background: "#F4C542", color: "#000" };
  const input = { width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc" };
  const tab = (active) => ({ padding: "8px 10px", borderRadius: 8, fontWeight: 700, cursor: "pointer", background: active ? "#00B074" : "#f5f5f5", color: active ? "#fff" : "#333" });

  // If signed in as a different email than the invite one, tell them explicitly
  const wrongUser = userEmail && email && userEmail.toLowerCase() !== email.toLowerCase();

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    if (!email) { setErr("Email required"); return; }
    if (invitedEmail && email.toLowerCase() !== invitedEmail) {
        setErr(`This invite is for ${invitedEmail}. Please use that email.`);
        return;
    }
    if (mode === "signup") {
      if (!pw || pw.length < 6) { setErr("Password must be at least 6 characters"); return; }
      if (pw !== pw2) { setErr("Passwords do not match"); return; }
    } else {
      if (!pw) { setErr("Password required"); return; }
    }

    try {
      setBusy(true);
      if (mode === "signin") {
        await signInWithEmailAndPassword(auth, email, pw);
      } else {
        await createUserWithEmailAndPassword(auth, email, pw);
        // Optional but nice: send verification email
        if (auth.currentUser && !auth.currentUser.emailVerified) {
          try { await sendEmailVerification(auth.currentUser); } catch {}
        }
      }
      // After auth success, bounce back to the invite page if present
      const ret = sessionStorage.getItem("inviteReturn");
      if (ret) {
        sessionStorage.removeItem("inviteReturn");
        window.location.href = ret;
      } else {
        window.location.href = "/";
      }
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOutAndContinue() {
    setErr("");
    try {
      await signOut(auth);
      // After sign out, we stay on this screen so they can proceed
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  return (
    <div style={card}>
      <h3 style={{ marginTop: 0 }}>Trelli account</h3>

      {wrongUser && (
        <div style={{ background: "#fff8e1", border: "1px solid #ffe082", padding: 12, borderRadius: 8, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>You're signed in as {userEmail}</div>
          <div>This invite is for <strong>{email}</strong>. Sign out and continue to create or sign in with that email.</div>
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button style={{ ...btn, background: "#444" }} onClick={handleSignOutAndContinue}>Sign out</button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button type="button" style={tab(mode === "signin")} onClick={() => setMode("signin")}>Sign in</button>
        <button type="button" style={tab(mode === "signup")} onClick={() => setMode("signup")}>Create account</button>
      </div>

      <form onSubmit={handleSubmit}>
        <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>Email</label>
        <input
            style={{ ...input, background: "#f9f9f9" }}
            type="email"
            value={email}
            readOnly
            placeholder="you@example.com"
            title="Email is locked to the invited address for security"
        />
        {invitedEmail && (
            <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                This invite is for <strong>{invitedEmail}</strong>. To use a different email, ask the head of household to send a new invite.
            </div>
        )}

        <label style={{ display: "block", fontWeight: 700, margin: "12px 0 6px" }}>Password</label>
        <input style={input} type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" />

        {mode === "signup" && (
          <>
            <label style={{ display: "block", fontWeight: 700, margin: "12px 0 6px" }}>Confirm password</label>
            <input style={input} type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="••••••••" />
          </>
        )}

        {err && <div style={{ color: "#b00020", marginTop: 10 }}>{err}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button type="submit" style={btn} disabled={busy}>
            {busy ? "Please wait…" : (mode === "signin" ? "Sign in" : "Create account")}
          </button>
          <button
            type="button"
            style={btnAlt}
            onClick={() => {
              const ret = sessionStorage.getItem("inviteReturn");
              window.location.href = ret || "/";
            }}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
